// Deciding whether to activate a waiting service worker, across every open
// window.
//
// The order matters and is the whole point:
//
//   1. count the clients  — from the service worker, which is the only thing
//                           that can enumerate them, so a window opened a
//                           second ago is counted too
//   2. poll them          — each replies with what it would lose
//   3. RECHECK            — immediately before activating, because a tab can
//                           start typing between the poll and the click, and
//                           activation is the moment that actually destroys
//                           work
//   4. activate           — send SKIP_WAITING, then reload THIS WINDOW only
//
// Two polls narrow the race; they do not close it. A tab can turn dirty or
// open after the final reply, so this must never be the only protection:
//
//   * Activation NEVER reloads another window. Only the window that asked
//     reloads, and only after its own guard agrees. There is deliberately no
//     broadcast "reload now", and deliberately no reload on controllerchange
//     — that classic one-liner reloads every open window, which is precisely
//     the payroll-losing behaviour this exists to prevent.
//   * The new worker must therefore stay compatible with already-open
//     clients. It is static-only and changes no API behaviour, so an old
//     page controlled by a new worker keeps working.
//
// Everything is injected so the rule can be tested without opening browsers.

import { decideConsensus, consensusReason, type TabReply } from './tabConsensus';

export interface UpdatePorts {
  /** How many windows exist right now, asked of the service worker. */
  countClients: () => Promise<number>;
  /** Broadcast a poll and gather replies until the timeout. */
  poll: (expected: number) => Promise<{ replies: TabReply[]; elapsedAll: boolean }>;
  /** Tell the waiting worker to take over. */
  activate: () => Promise<void>;
  /** Reload this window once the new worker controls the page. */
  reload: () => void;
}

export type UpdateOutcome =
  | { kind: 'activated' }
  | { kind: 'deferred'; reason: string; retryable: true };

export const runUpdate = async (ports: UpdatePorts): Promise<UpdateOutcome> => {
  const first = await check(ports);
  if (first) return first;

  // The recheck. Between the poll and this moment someone may have started
  // typing, or a new window may have opened — and this is the instant the
  // work would actually be lost, not the instant we asked.
  const second = await check(ports);
  if (second) return second;

  await ports.activate();
  ports.reload();
  return { kind: 'activated' };
};

const check = async (ports: UpdatePorts): Promise<UpdateOutcome | null> => {
  const expected = await ports.countClients();
  const { replies, elapsedAll } = await ports.poll(expected);
  const consensus = decideConsensus(replies, expected, elapsedAll);
  if (consensus.safe) return null;
  return {
    kind: 'deferred',
    // Always retryable: the blocking tab may finish, or be closed.
    reason: consensusReason(consensus) ?? 'Another window may have unsaved work.',
    retryable: true,
  };
};
